import React, { useRef, useState } from 'react';
import useAnchoredPosition from './useAnchoredPosition';

// Small "i" info icon with a hover/focus tooltip bubble. Replaces always-visible
// helper text under form fields. Lives inline next to (often uppercase) labels,
// so both the trigger glyph and the bubble reset text-transform/letter-spacing.
//
// The bubble is positioned FIXED by useAnchoredPosition rather than absolute:
// every host that shows these tips has a clipping ancestor — AgentPopup (modal
// `overflow:hidden`, body `overflowY:auto`), AgentSettingsModal (same shape)
// and shared/Modal.jsx (body `overflowY:auto`) — and a centred absolute bubble
// wants ~130px to the left of a 14px icon, so it was being cut off by all
// three. `place` is now a PREFERENCE: the hook flips it when the chosen side is
// short, and clamps the bubble into the viewport when centring on the trigger
// would push it past an edge.
//
// No portal (the repo has none): fixed positioning already escapes the clip,
// and z-index only has to beat siblings inside the topmost modal's own stacking
// context. A portal would add focus/unmount plumbing for no gain.

const s = {
  wrap: {
    // No longer needed for the bubble (it is fixed), but kept so the icon's
    // inline layout next to the label is exactly what it was.
    position: 'relative', display: 'inline-flex', alignItems: 'center',
    marginLeft: '6px', textTransform: 'none', letterSpacing: 'normal',
  },
  trigger: {
    width: '14px', height: '14px', borderRadius: '50%', flexShrink: 0,
    border: '1px solid var(--text-muted)', color: 'var(--text-muted)',
    fontSize: '9px', fontWeight: 700, lineHeight: 1, fontStyle: 'normal',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'help', background: 'transparent', userSelect: 'none',
  },
  // Position/left/top/bottom/visibility are owned by the hook and spread over
  // this. The max-width cap stays here because it must apply identically in the
  // hook's measuring pass and its final pass — it is the single source of truth
  // for how wide the bubble gets, and the hook clamps against the MEASURED
  // result rather than recomputing it.
  bubble: (width) => ({
    width: 'max-content', maxWidth: `min(${width}px, calc(100vw - 16px))`,
    background: '#1e293b', color: '#fff',
    fontSize: '11px', fontWeight: 400, lineHeight: 1.5,
    padding: '8px 10px', borderRadius: '8px',
    boxShadow: '0 6px 20px rgba(15,23,42,0.25)',
    zIndex: 10000, whiteSpace: 'normal', textAlign: 'left',
    textTransform: 'none', letterSpacing: 'normal', pointerEvents: 'none',
  }),
};

export default function InfoTip({ text, width = 260, place = 'top' }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const bubbleRef = useRef(null);
  // Called before the early return below so hook order stays stable.
  const position = useAnchoredPosition(triggerRef, bubbleRef, open, { place });
  if (!text) return null;
  return (
    <span style={s.wrap}>
      <span
        ref={triggerRef}
        style={s.trigger}
        tabIndex={0}
        aria-label={text}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        i
      </span>
      {open && (
        <span ref={bubbleRef} role="tooltip" style={{ ...s.bubble(width), ...position }}>
          {text}
        </span>
      )}
    </span>
  );
}
